import CssBaseline from "@material-ui/core/CssBaseline";
import { createMuiTheme, MuiThemeProvider } from "@material-ui/core/styles";
import createBreakpoints from "@material-ui/core/styles/createBreakpoints";
import CancelIcon from "@material-ui/icons/CancelRounded";
import SuccessIcon from "@material-ui/icons/CheckCircleRounded";
import ErrorIcon from "@material-ui/icons/ErrorRounded";
import InfoIcon from "@material-ui/icons/InfoRounded";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDownRounded";
import StarRateIcon from "@material-ui/icons/StarRateRounded";
import WarningIcon from "@material-ui/icons/WarningRounded";
import React from "react";
import { Color } from "./color";
import { SvgIconSize } from "./icon";

const fontStack = ["Arial", "sans-serif"];

const breakpoints = createBreakpoints({
    values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1280,
        xl: 1600,
    },
});

export const muiTheme = createMuiTheme({
    palette: {
        background: {
            default: Color.white,
        },
        primary: {
            main: Color.blue1,
            contrastText: Color.white,
        },
        secondary: {
            main: Color.blue2,
            contrastText: Color.grey1,
        },
        error: {
            main: Color.red1,
        },
        text: {
            primary: Color.grey6,
            secondary: Color.grey5,
        },
        action: {
            focus: Color.focus10,
            hover: Color.focus10,
            selected: Color.grey2,
        },
    },
    breakpoints: {
        ...breakpoints,
    },
    props: {
        MuiAlert: {
            iconMapping: {
                error: <ErrorIcon />,
                info: <InfoIcon />,
                success: <SuccessIcon />,
                warning: <WarningIcon />,
            },
        },
        MuiAutocomplete: {
            fullWidth: true,
            closeIcon: <CancelIcon />, // will be renamed to `clearIcon` in `v5.0.0`
            popupIcon: <KeyboardArrowDownIcon />,
            size: "small",
        },
        MuiBadge: {
            overlap: "circle",
        },
        MuiButtonBase: {
            disableRipple: true,
            disableTouchRipple: true,
        },
        MuiButton: {
            disableElevation: true,
        },
        MuiCheckbox: {
            color: "primary",
            disableRipple: true,
        },
        MuiCircularProgress: {
            size: 24,
            thickness: 4,
        },
        MuiDialog: {
            PaperProps: {
                elevation: 2,
            },
            fullWidth: true,
        },
        MuiDialogActions: {
            disableSpacing: false,
        },
        MuiRadio: {
            color: "primary",
        },
        MuiRating: {
            icon: <StarRateIcon fontSize="inherit" />,
            size: "large",
        },
        MuiSnackbar: {
            anchorOrigin: {
                horizontal: "right",
                vertical: "top",
            },
        },
        MuiSwitch: {
            color: "primary",
        },
        MuiTextField: {
            fullWidth: true,
            size: "small",
            variant: "filled",
        },
    },
    typography: {
        fontFamily: fontStack.join(","),
        allVariants: {
            fontFamily: fontStack.join(","),
            letterSpacing: "normal",
        },
        h1: {
            fontSize: 34,
            fontWeight: "bold",
            lineHeight: "1em",
        },
        h2: {
            fontSize: 34,
            lineHeight: "1em",
        },
        h3: {
            fontSize: 22,
            fontWeight: "bold",
            lineHeight: "1em",
        },
        h4: {
            fontSize: 22,
            lineHeight: "1em",
        },
        h5: {
            fontSize: 16,
            fontWeight: "bold",
            lineHeight: "1em",
        },
        h6: {
            fontSize: 16,
            lineHeight: "1em",
        },
        body1: {
            fontSize: 14,
            lineHeight: "20px",
        },
        body2: {
            fontSize: 11,
            lineHeight: "14px",
        },
        overline: {
            fontSize: 10,
            lineHeight: "1em",
            textTransform: "uppercase",
        },
        button: {
            fontSize: 12,
            lineHeight: "1em",
            textTransform: "none",
        },
    },
    overrides: {
        MuiAlert: {
            root: {
                alignItems: "center",
                borderRadius: 3,
            },
            filledError: {
                backgroundColor: Color.red1,
                color: Color.white,
            },
            filledInfo: {
                backgroundColor: Color.blue2,
                color: Color.white,
                "& > $action > .MuiIconButton-root": {
                    // Focus and hover style is explicitly discussed and set with FMe
                    "&:focus, &:hover": {
                        backgroundColor: Color.focus30, // darker focus color because of darker background
                    },
                },
            },
            filledSuccess: {
                backgroundColor: Color.green1,
                color: Color.white,
            },
            filledWarning: {
                backgroundColor: Color.yellow1,
                color: Color.grey6,
            },
            icon: {
                opacity: 1, // override default 0.9
            },
            message: {
                fontSize: "14px",
                lineHeight: "20px",
            },
            action: {
                marginRight: -8,
                paddingLeft: 8,
                "& > .MuiIconButton-root": {
                    padding: 8,
                    // Focus and hover style is explicitly discussed and set with FMe
                    "&:focus, &:hover": {
                        backgroundColor: Color.focus20,
                        color: "inherit",
                    },
                },
            },
        },
        MuiAutocomplete: {
            clearIndicator: {
                marginRight: -6, // override
                padding: 6,
                visibility: "visible",
            },
            endAdornment: {
                position: "static", // override "absolute" which comes for ourselves, see `combobox/EndAdornment.tsx`
            },
            inputRoot: {
                '&[class*="MuiFilledInput-root"]': {
                    paddingLeft: 12,
                    paddingRight: 48,
                    "$hasPopupIcon &, $hasClearIcon &": {
                        paddingRight: 80,
                    },
                    "$hasPopupIcon$hasClearIcon &": {
                        paddingRight: 80,
                    },
                },
                '&[class*="MuiFilledInput-root"][class*="MuiFilledInput-marginDense"]': {
                    "& $input": {
                        padding: "5.5px 4px",
                    },
                },
                "& $input": {
                    minWidth: 5, // fill the last remaining `10%` of `inputTextSpace` in `SingleComboBox.tsx` but override the default of `30`
                },
            },
            option: {
                minHeight: "auto",
                '&[data-focus="true"]': {
                    backgroundColor: Color.focus10,
                },
                '&[aria-selected="true"]': {
                    backgroundColor: Color.white,
                    fontWeight: "bold",
                },
            },
            paper: {
                border: `1px solid ${Color.grey2}`,
                borderRadius: "0 0 3px 3px",
                boxShadow: "0 10px 10px 0px rgba(50, 50, 50, 0.2)", // Color.grey6
                fontSize: 12,
                margin: 0,
            },
            popupIndicator: {
                marginRight: 8, // override the original value of `-2px`
                margin: 6,
                padding: 6, // override the original value of `2px`
            },
        },
        MuiBackdrop: {
            root: {
                backgroundColor: "rgba(50, 50, 50, 0.8)", // grey6 opacity 80%
            },
        },
        MuiBadge: {
            badge: {
                backgroundColor: Color.red1,
                borderRadius: 8,
                color: Color.white,
                fontSize: 10,
                height: 16,
                lineHeight: "1em",
                minWidth: 16,
                padding: "0 4px",
            },
            colorPrimary: {
                backgroundColor: Color.blue1,
            },
        },
        MuiBreadcrumbs: {
            root: {
                flexGrow: 1,
                fontSize: "12px",
                lineHeight: "1em",
                "& a": {
                    color: Color.grey5,
                    "&:hover": {
                        color: Color.blue1,
                        textDecoration: "none",
                    },
                    "&$disabled": {
                        color: Color.grey4,
                        textDecoration: "none",
                    },
                },
            },
            separator: {
                color: Color.grey5,
                marginLeft: "2px",
                marginRight: "2px",
            },
        },
        MuiButton: {
            root: {
                borderRadius: 3,
                height: "32px",
                padding: "8px 16px",
            },
            label: {
                fontSize: "12px",
                fontWeight: "bold",
                lineHeight: "12px",
            },
            // Page Action Highlight Button -- https://doc.supplyon.de/display/S10071/Page+Action+Highlight+Button
            contained: {
                backgroundColor: Color.green1,
                color: Color.white,
                "&:hover": {
                    "&$disabled": {
                        backgroundColor: Color.grey3, // just for overriding default
                    },
                },
                "&:hover:not(.Mui-disabled), &.Mui-focusVisible": {
                    backgroundColor: Color.green2,
                    color: Color.white,
                },
                "&$disabled": {
                    backgroundColor: Color.grey3,
                    color: Color.white,
                },
            },
            // Page Action Button -- https://doc.supplyon.de/display/S10071/Page+Action+Button
            outlined: {
                backgroundColor: Color.white,
                borderColor: Color.blue1,
                color: Color.blue1,
                "&:hover:not(.Mui-disabled), &.Mui-focusVisible": {
                    backgroundColor: Color.focus10,
                    borderColor: Color.blue3,
                    color: Color.blue3,
                },
                "&$disabled": {
                    backgroundColor: Color.white,
                    borderColor: Color.grey3,
                    color: Color.grey4,
                },
            },
            // Table Action Highlight Button -- https://doc.supplyon.de/display/S10071/Table+Action+Highlight+Button
            containedSecondary: {
                backgroundColor: Color.grey5,
                color: Color.white,
                padding: "6px 16px", // 6px instead of 8px because of 20px icon
                "&:hover:not(.Mui-disabled), &.Mui-focusVisible": {
                    backgroundColor: Color.blue1,
                    color: Color.white,
                },
                "&$disabled": {
                    backgroundColor: Color.grey3,
                    color: Color.white,
                },
            },
            // Table Action Button -- https://doc.supplyon.de/display/S10071/Table+Action+Button
            outlinedSecondary: {
                backgroundColor: Color.white,
                borderColor: Color.grey5,
                color: Color.grey5,
                padding: "6px 16px", // 6px instead of 8px because of 20px icon
                "&:hover:not(.Mui-disabled), &.Mui-focusVisible": {
                    backgroundColor: Color.focus10,
                    borderColor: Color.blue1,
                    color: Color.blue1,
                },
                "&$disabled": {
                    backgroundColor: Color.white,
                    borderColor: Color.grey3,
                    color: Color.grey4,
                },
            },
            // Contextual Button -- https://doc.supplyon.de/display/S10071/Contextual+button
            text: {
                color: Color.grey5,
                padding: 8,
                "&:hover, &.Mui-focusVisible": {
                    color: Color.blue1,
                    backgroundColor: Color.focus10,
                },
                "&$disabled": {
                    color: Color.grey4,
                    "& .MuiButton-startIcon, & .MuiButton-endIcon": {
                        color: Color.grey3,
                    },
                },
            },
        },
        MuiCardActions: {
            root: {
                justifyContent: "flex-start",
                padding: "0 24px 0 24px",
            },
            spacing: {
                "& > :first-child": {
                    margin: "16px 0",
                },
                "& > :not(:first-child)": {
                    marginLeft: undefined, // unset it
                    margin: "16px 0 16px 24px",
                },
            },
        },
        MuiCardContent: {
            root: {
                padding: "24px 24px 32px 24px",
            },
        },
        MuiCardHeader: {
            root: {
                padding: 24,
            },
            action: {
                marginBottom: -8,
            },
        },
        MuiCheckbox: {
            root: {
                padding: 8, // override internal `PrivateSwitchBase`s `padding` of `9px`
                transition: "color 0.4s ease",
            },
            colorPrimary: {
                color: Color.grey5,
                "&$checked": {
                    color: Color.blue1,
                    backgroundColor: "transparent", // `unset` not supported in IE
                    "&:hover": {
                        backgroundColor: Color.focus10,
                    },
                },
                "&:hover": {
                    backgroundColor: Color.focus10,
                },
                "&$disabled": {
                    color: Color.grey3,
                    "&$checked": {
                        color: Color.grey4,
                    },
                },
                "&.Mui-focusVisible": {
                    backgroundColor: Color.focus10,
                },
            },
        },
        MuiChip: {
            // root is always applied and therefore also size="medium"
            root: {
                color: Color.grey5,
                fontSize: "12px",
                margin: 0,
                padding: "0 4px 0 4px",
                "&$disabled": {
                    color: Color.grey4,
                    opacity: "none",
                },
                "&$disabled > $icon, &$disabled > $deleteIcon": {
                    color: Color.grey4,
                },
            },
            outlined: {
                border: `1px solid ${Color.grey3}`,
                backgroundColor: Color.white,
                "& $icon": {
                    marginLeft: 8, // 12px minus 4px padding of container
                },
                "& $iconSmall": {
                    marginLeft: 8,
                },
                // Action icon in our case ;-)
                "& $deleteIcon": {
                    marginRight: 8, // 12px minus 4px padding of container
                },
                "& $deleteIconSmall": {
                    marginRight: 8,
                },
                "$clickable&:focus, $clickable&:hover, $deletable&:focus, $deletable&:hover": {
                    backgroundColor: Color.focus10, // override default
                },
            },
            sizeSmall: {
                height: 20,
                padding: "4px 0 4px 0",
            },
            label: {
                flexGrow: 1,
                paddingLeft: 8,
                paddingRight: 8,
            },
            labelSmall: {},
            icon: {
                color: Color.grey5,
                height: "1em",
                marginRight: 0,
                width: "1em",
                // "disabled" style in "root" because "svg" gets no "disabled" property
            },
            iconSmall: {
                height: 16,
                marginRight: 0,
                width: 16,
            },
            // Action icon in our case ;-)
            deleteIcon: {
                color: Color.grey5,
                height: "1em",
                marginLeft: 0,
                width: "1em",
                "&:hover": {
                    color: Color.blue1,
                },
                // "disabled" style in "root" because "svg" gets no "disabled" property
            },
            deleteIconSmall: {
                height: 16,
                marginLeft: 0,
                width: 16,
            },
        },
        MuiDialog: {
            paper: {
                margin: 16,
            },
            paperWidthXl: {
                maxWidth: 1808,
            },
            paperWidthLg: {
                maxWidth: 1504,
            },
            paperWidthMd: {
                maxWidth: 1200,
            },
            paperWidthSm: {
                maxWidth: 896,
            },
            paperWidthXs: {
                maxWidth: 592,
            },
        },
        MuiDialogContent: {
            root: {
                padding: "24px 24px 32px 24px",
            },
        },
        MuiDialogActions: {
            root: {
                justifyContent: "flex-start",
                padding: "0 24px 0 24px",
            },
            spacing: {
                "& > :first-child": {
                    margin: "16px 0",
                },
                "& > :not(:first-child)": {
                    marginLeft: undefined, // unset it
                    margin: "16px 0 16px 24px",
                },
            },
        },
        MuiDialogTitle: {
            root: {
                padding: 24,
                // uses h2 with `Typography` in variant `h6` which is not bold but has correct size
                "& h2": {
                    fontWeight: "bold",
                },
            },
        },
        MuiDivider: {
            root: {
                backgroundColor: Color.grey3,
            },
        },
        MuiDrawer: {
            paperAnchorDockedLeft: {
                borderRight: "none",
                boxShadow: `0 0 2px 0 ${Color.grey4}`,
            },
        },
        MuiFilledInput: {
            root: {
                backgroundColor: Color.grey1,
                color: Color.grey6,
                "&$disabled": {
                    backgroundColor: Color.grey1,
                    color: Color.grey5,
                },
                "&:hover": {
                    backgroundColor: Color.focus10,
                },
                "&$focused": {
                    backgroundColor: Color.focus10,
                },
                // use custom pseudo-class for required
                "&.required:not($disabled)": {
                    backgroundColor: Color.grey2,
                    "&:hover": {
                        backgroundColor: Color.grey2,
                    },
                    "&$focused": {
                        backgroundColor: Color.grey2,
                    },
                },
            },
            adornedEnd: {
                "&$multiline > .MuiInputAdornment-filled": {
                    position: "absolute",
                    padding: 0,
                    right: 12,
                    top: 24,
                },
            },
            input: {
                padding: "27px 16px 10px",
            },
            inputAdornedEnd: {
                marginRight: -14,
            },
            inputMarginDense: {
                paddingTop: 25,
            },
            inputMultiline: {
                padding: "0px 4px",
                "&$inputAdornedEnd": {
                    marginRight: 32,
                },
            },
            underline: {
                // see https://github.com/mui-org/material-ui/blob/master/packages/material-ui/src/Input/Input.js for actual implementation
                "&:after": {
                    borderBottom: 0,
                },
                "&$error:after": {
                    borderBottom: `2px solid ${Color.red1}`,
                    transform: "scaleX(0)",
                },
                "&$error$focused:after": {
                    transform: "scaleX(1)",
                },
                "&$focused:after": {
                    borderBottom: `2px solid ${Color.blue1}`,
                },
                "&:before": {
                    borderBottom: 0,
                },
                "&:hover:not($disabled):before": {
                    borderBottom: 0,
                },
                "&$disabled:before": {
                    borderBottomStyle: "none",
                },
            },
        },
        MuiFormControlLabel: {
            root: {
                marginLeft: 0,
            },
            label: {
                "&$disabled": {
                    color: Color.grey5,
                },
            },
        },
        MuiFormGroup: {
            root: {
                "& > *": {
                    marginLeft: -8,
                    "& > .MuiRating-root": {
                        // special handling for rating with has no icon with `padding: 8` (needs to be done like that because first appearance of `*Rating*` is deeper nested)
                        marginLeft: 8,
                    },
                },
                "& + .MuiFormHelperText-root": {
                    marginBottom: 32, // additional margin for helper text in order to separate grouped controls better from other components according to style guide
                },
            },
        },
        MuiFormHelperText: {
            root: {
                color: Color.grey6,
                display: "flex",
                fontSize: 11,
                height: 32,
                lineHeight: "16px",
                marginTop: 4,
                whiteSpace: "nowrap",
                "&$error": {
                    color: Color.red1,
                },
                "&$disabled": {
                    color: Color.grey5,
                },
            },
            contained: {
                lineHeight: "14px",
                marginLeft: 16,
                marginRight: 16,
            },
            marginDense: {
                marginTop: 0,
                paddingTop: 5,
            },
        },
        MuiFormLabel: {
            root: {
                color: Color.grey6,
                marginBottom: 12,
                "&:not(.MuiInputLabel-root)": {
                    fontWeight: "bold", // for FormLabel not used within fields
                },
                "&$focused": {
                    color: Color.grey6,
                },
                "&$disabled": {
                    color: Color.grey5,
                },
            },
        },
        MuiIcon: {
            root: {
                fontSize: SvgIconSize.medium,
            },
        },
        MuiIconButton: {
            root: {
                color: Color.grey5,
                padding: 6, // `6px` instead of normally `8px` because of icon size, see `SvgIconSize`
                transition: "color 0.4s ease",
                "&:hover, &.Mui-focusVisible": {
                    color: Color.blue1,
                    backgroundColor: Color.focus10,
                },
                "&$disabled": {
                    color: Color.grey3,
                },
            },
            colorPrimary: {
                "&:hover, &.Mui-focusVisible": {
                    backgroundColor: Color.focus10,
                },
            },
            edgeEnd: {
                marginRight: -6,
            },
            edgeStart: {
                marginLeft: -6,
            },
        },
        // Cell editor field with `variant="standard"` (for normal `TextField` with `variant="filled"` this is overridden by `MuiFilledInput`)
        MuiInput: {
            inputMarginDense: {
                fontSize: 12,
                paddingBottom: 4,
                paddingTop: 2,
            },
            underline: {
                "&:after": {
                    borderBottom: `1px solid ${Color.grey4}`,
                },
                "&$error:before": {
                    borderBottom: `1px solid ${Color.red1}`,
                },
                "&$focused:after": {
                    borderBottom: `1px solid ${Color.blue1}`,
                },
                "&:hover:not($disabled):before": {
                    borderBottom: `1px solid ${Color.blue1}`,
                },
                "&$disabled:before": {
                    borderBottomStyle: "solid",
                },
            },
        },
        MuiInputAdornment: {
            marginDense: {
                "&:not($filled) svg": {
                    fontSize: 18,
                },
            },
            positionEnd: {
                marginLeft: 16,
                marginRight: -12,
            },
        },
        MuiInputLabel: {
            root: {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "calc(100% - 56px)", // must be set because of labels `absolute` positioning, otherwise grows larger than screen width
            },
            filled: {
                "&$marginDense": {
                    transform: "translate(16px, 17px) scale(1)",
                },
                "&$shrink": {
                    transform: "translate(16px, 10px) scale(0.75)",
                    "&$marginDense": {
                        transform: "translate(16px, 9px) scale(0.75)",
                    },
                },
            },
        },
        MuiLinearProgress: {
            root: {
                borderRadius: 3,
                height: 8,
            },
            colorPrimary: {
                backgroundColor: Color.grey2,
            },
            colorSecondary: {
                backgroundColor: Color.grey2,
            },
        },
        MuiLink: {
            root: {
                fontWeight: "bold",
                "&:hover": {
                    color: Color.blue3,
                },
            },
            underlineHover: {
                "&:hover": {
                    textDecoration: "none",
                },
            },
        },
        MuiListItem: {
            root: {
                "&$selected, &$selected:focus, &$selected:hover": {
                    backgroundColor: Color.grey2,
                },
                "&$disabled": {
                    color: Color.grey5,
                    opacity: 1,
                },
            },
        },
        MuiListItemIcon: {
            root: {
                color: Color.grey5,
                minWidth: 24,
            },
        },
        MuiListItemText: {
            root: {
                marginBottom: 0,
                marginTop: 0,
            },
            primary: {
                fontSize: 12,
                lineHeight: "20px",
            },
        },
        MuiMenu: {
            paper: {
                boxShadow: "0 10px 10px 0 rgba(50, 50, 50, 0.2);", // not `... ${ Color.grey6 }33;` because of IE support, https://developer.mozilla.org/en-US/docs/Web/CSS/color
            },
        },
        MuiMenuItem: {
            root: {
                // override defaults if `MenuItem` is used directly (= without `ListItemText`)
                fontSize: 12,
                lineHeight: "20px",
                minHeight: 32,
            },
        },
        MuiPaper: {
            elevation1: {
                boxShadow: `0 0 2px ${Color.grey4}`,
            },
            rounded: {
                borderRadius: 3,
            },
        },
        MuiRating: {
            root: {
                color: Color.blue1,
                fontSize: SvgIconSize.medium,
                margin: "8px 8px 8px 0",
                "&$disabled": {
                    color: Color.grey5,
                    opacity: 1, // `unset` not supported in IE
                    pointerEvents: "none",
                },
                "&$focusVisible $iconActive": {
                    outline: "none",
                },
            },
            sizeSmall: {
                fontSize: SvgIconSize.small,
            },
            sizeLarge: {
                fontSize: SvgIconSize.large,
            },
            icon: {
                margin: "-4px", // reduce `MuiSvgIcon`s default margins...
            },
            iconEmpty: {
                color: Color.grey3,
            },
            iconActive: {
                backgroundColor: Color.focus10,
                borderRadius: "50%",
                transform: "none",
            },
        },
        MuiPickersBasePicker: {
            container: {
                padding: "8px 16px",
            },
            pickerView: {
                minHeight: 0,
                minWidth: 0,
            },
        },
        MuiPickersCalendar: {
            transitionContainer: {
                marginTop: 0, // override
                minHeight: 144, // uses flexbox and requires fixed height :-(
            },
        },
        MuiPickersCalendarHeader: {
            daysHeader: {
                height: 24,
                maxHeight: 24, // override
            },
            dayLabel: {
                color: Color.grey6,
                fontSize: 12,
                fontWeight: "bold",
                lineHeight: "24px",
                margin: 0, // override
                width: 24, // override
            },
            iconButton: {
                padding: 0,
            },
            switchHeader: {
                marginBottom: 8,
                marginTop: 8,
            },
            transitionContainer: {
                height: 12,
                "& > *": {
                    fontSize: 12,
                    fontWeight: "bold",
                    lineHeight: "12px",
                },
            },
        },
        MuiPickersDay: {
            day: {
                width: 24,
                height: 24,
                margin: 0, // override
                fontSize: 12,
                fontWeight: "normal",
            },
            daySelected: {
                "&:hover": {
                    color: Color.white, // override default which is `blue1` on `blue1` background...
                },
            },
        },
        MuiPickersStaticWrapper: {
            staticWrapperRoot: {
                minWidth: 0, // override default 310px
            },
        },
        MuiRadio: {
            root: {
                padding: 8, // override internal `PrivateSwitchBase`s `padding` of `9px`
            },
            colorPrimary: {
                color: Color.grey5,
                "&$checked": {
                    color: Color.blue1,
                    "&:hover": {
                        backgroundColor: Color.focus10,
                    },
                },
                "&:hover": {
                    backgroundColor: Color.focus10,
                },
                "&$disabled": {
                    color: Color.grey3,
                    "&$checked": {
                        color: Color.grey4,
                    },
                },
                "&.Mui-focusVisible": {
                    backgroundColor: Color.focus10,
                },
            },
        },
        MuiSnackbar: {
            root: {
                maxWidth: "calc(50% - 48px)", // half of screen minus protection area
                minWidth: 240,
            },
        },
        MuiSvgIcon: {
            root: {
                fontSize: SvgIconSize.medium,
            },
        },
        MuiSwitch: {
            colorPrimary: {
                "&$checked": {
                    color: Color.white,
                    "&:hover": {
                        backgroundColor: Color.focus10, // override default
                    },
                },
                "&$disabled": {
                    color: Color.white,
                    "& + $track": {
                        backgroundColor: Color.grey3,
                    },
                },
            },
            switchBase: {
                color: Color.white,
                marginLeft: "1px",
                padding: "14px",
                "& + $track": {
                    // off, enabled
                    backgroundColor: Color.grey5,
                    borderColor: Color.grey5,
                },
                "&$checked": {
                    transform: "translateX(18px)",
                    color: Color.white,
                    "& + $track": {
                        // on, enabled
                        opacity: 1,
                        backgroundColor: Color.blue1,
                        borderColor: Color.blue1,
                    },
                },
                "&$disabled": {
                    "& + $track": {
                        // off, disabled
                        backgroundColor: Color.grey3,
                        borderColor: Color.grey3,
                        opacity: 1,
                    },
                    "&$checked + $track": {
                        // on, disabled
                        backgroundColor: Color.grey4,
                        borderColor: Color.grey4,
                    },
                },
                "&:hover": {
                    color: Color.white,
                    backgroundColor: Color.focus10,
                },
                "&.Mui-focusVisible": {
                    backgroundColor: Color.focus10,
                },
            },
            thumb: {
                width: 10,
                height: 10,
                boxShadow: "none",
            },
            track: {
                border: `1px solid`,
                // tslint:disable-next-line:no-magic-numbers
                borderRadius: 16 / 2,
                opacity: 1,
                backgroundColor: Color.grey5,
            },
            checked: {},
        },
        MuiTableCell: {
            root: {
                fontSize: "12px",
            },
        },
        MuiTab: {
            root: {
                fontSize: "16px",
                fontWeight: "bold",
                minWidth: 48,
                padding: "24px 24px 20px 24px",
                [breakpoints.up("sm")]: {
                    // override media query with same values as above
                    minWidth: 48,
                    padding: "24px 24px 20px 24px",
                },
                "&:hover": {
                    color: Color.blue3,
                },
                "&$selected": {
                    color: Color.grey5,
                },
            },
            textColorInherit: {
                color: Color.blue1,
                opacity: "unset",
                "&$disabled": {
                    color: Color.grey4, // clarified with Florian Meyer but use case should not occur and therefore not part of the styleguide
                    opacity: "unset",
                },
            },
        },
        MuiTabs: {
            indicator: {
                backgroundColor: Color.grey5,
                height: 4,
            },
        },
        MuiTooltip: {
            tooltip: {
                backgroundColor: Color.blue1,
                borderRadius: 3,
                color: Color.white,
                fontSize: "11px",
                lineHeight: "14px",
                maxWidth: 150,
                padding: "8px",
                textAlign: "center",
            },
            tooltipPlacementLeft: {
                margin: 8,
                [breakpoints.up("sm")]: {
                    margin: 8,
                },
            },
            tooltipPlacementRight: {
                margin: 8,
                [breakpoints.up("sm")]: {
                    margin: 8,
                },
            },
            tooltipPlacementTop: {
                margin: 8,
                [breakpoints.up("sm")]: {
                    margin: 8,
                },
            },
            tooltipPlacementBottom: {
                margin: 8,
                [breakpoints.up("sm")]: {
                    margin: 8,
                },
            },
        },
        MuiTypography: {
            h1: {
                "&$gutterBottom": {
                    margin: "32px 0 32px 0",
                },
            },
            h2: {
                "&$gutterBottom": {
                    margin: "32px 0 32px 0",
                },
            },
            h3: {
                "&$gutterBottom": {
                    margin: "32px 0 32px 0",
                },
            },
            h4: {
                "&$gutterBottom": {
                    margin: "32px 0 32px 0",
                },
            },
            h5: {
                "&$gutterBottom": {
                    margin: "32px 0 16px 0",
                },
            },
            h6: {
                "&$gutterBottom": {
                    margin: "32px 0 16px 0",
                },
            },
            body1: {
                "&$gutterBottom": {
                    margin: "0 0 8px 0",
                },
            },
            body2: {
                "&$gutterBottom": {
                    margin: "0 0 8px 0",
                },
            },
            button: {
                fontSize: 12,
                lineHeight: "1em",
                textTransform: "none",
                "&$gutterBottom": {
                    margin: 0,
                },
            },
        },
    },
});

export const withTheme = <P extends unknown>(ui: React.ReactElement<P>): React.ReactElement<P> => (
    <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {ui}
    </MuiThemeProvider>
);
